import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { produce } from 'immer';
import {
  SButton,
  SIcon,
  SModal,
  SNotification,
  SSwitch,
} from '@chargebee/sting-react';
import {
  getGroupConditions,
  getGroupType,
  GroupType,
  type RuleTopLevelCondition,
} from '@bbkAdminComponents/experiences/audiences/edit-rule/rules-utils';
import {
  Chip,
  formatConditions,
  FormatConditionValues,
} from '@bbkAdminComponents/optimizer/optimization-condition-utils';
import { OptimizationActionDrawer } from '@bbkAdminComponents/optimizer/OptimizationActionDrawer';
import { OptimizationActionForm } from '@bbkAdminComponents/optimizer/OptimizationActionForm';
import { useOptimizationEditor } from '@bbkAdminComponents/optimizer/OptimizationEditorContext';
import { selectAppId } from '@bbkAdminRedux/app/selectors';
import {
  OptimizationActionType,
  OptimizationPriceAdjustmentType,
  OptimizationSliceHelper,
  optimizationsSlice,
  type OptimizationAction,
} from '@bbkAdminRedux/rtkq/optimizations.slice';
import { AuthorizedPaths } from '@bbkAdminRouter/router-paths';
import { useUrl } from '@bbkAdminUtils/nav';
import { Route, useHistory, useParams } from '@bbkShared/react-router';
import { OptimizationActionsHelper } from './OptimizationActionsHelper';

// Format action summary for collapsed state
const formatActionSummary = (action: OptimizationAction): string => {
  if (action.type === OptimizationActionType.PLAN_PRICE_ADJUSTMENT) {
    const adjustmentTypeLabels: Record<
      OptimizationPriceAdjustmentType,
      string
    > = {
      [OptimizationPriceAdjustmentType.INCREASE_BY_PERCENTAGE]:
        'increase Plan Price by',
      [OptimizationPriceAdjustmentType.INCREASE_BY_FIXED_AMOUNT]:
        'increase Plan Price by',
      [OptimizationPriceAdjustmentType.DECREASE_BY_PERCENTAGE]:
        'decrease Plan Price by',
      [OptimizationPriceAdjustmentType.DECREASE_BY_FIXED_AMOUNT]:
        'decrease Plan Price by',
      [OptimizationPriceAdjustmentType.SET_FIXED_PRICE]: 'set Plan Price to',
    };

    const isPercentage =
      action.adjustment_type ===
        OptimizationPriceAdjustmentType.INCREASE_BY_PERCENTAGE ||
      action.adjustment_type ===
        OptimizationPriceAdjustmentType.DECREASE_BY_PERCENTAGE;

    const label = adjustmentTypeLabels[action.adjustment_type] || '';
    const value = action.adjustment_value ?? 0;
    const unit = isPercentage ? '%' : '';

    return `then ${label} ${value}${unit}`;
  }
  if (action.type === OptimizationActionType.PLAN_CHANGE) {
    const planCount = action.plans.length;
    if (planCount === 0) {
      return 'then change plans';
    }
    if (planCount === 1) {
      return 'then change plans';
    }
    return `then change ${planCount} plans`;
  }
  return 'then adjust addon prices';
};

// Format condition summary for collapsed state
const formatConditionSummary = (condition: RuleTopLevelCondition): string => {
  const conditions = formatConditions(condition);
  if (conditions.length === 0) {
    return '';
  }
  if (conditions.length === 1) {
    return `When ${conditions[0]}`;
  }
  const groupType = getGroupType(condition);
  const connector = groupType === GroupType.AND ? 'and' : 'or';
  return `When ${conditions.join(` ${connector} `)}`;
};

// Format full summary line for collapsed state
const formatSummary = (
  action: OptimizationAction,
  condition: RuleTopLevelCondition
): string => {
  const conditionText = formatConditionSummary(condition);
  const actionText = formatActionSummary(action);
  return `${conditionText} ${actionText}`;
};

type ActionCardProps = {
  action: OptimizationAction;
  isExpanded?: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onRemove: () => void;
};

export const ActionCardDetails: React.FC<{ actions: OptimizationAction[] }> = ({
  actions,
}) => {
  const appKey = useSelector(selectAppId);

  const { data: plans = [] } = optimizationsSlice.endpoints.getPlans.useQuery(
    appKey ? { appKey } : skipToken
  );

  const getPlanLabel = useMemo(
    () => OptimizationActionsHelper.getPlanIdLabel(plans),
    [plans]
  );

  return (
    /* Two-column grid for Conditions and Actions */
    <div className="tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-2">
      {/* Conditions Section */}
      <div>
        <div className="tw-mb-2 tw-text-sm tw-font-medium tw-text-neutral-800">
          Conditions
        </div>
        <div>
          {actions.map((action, index) => {
            return <FormatConditionValues condition={action.condition} />;
          })}
        </div>
      </div>

      {/* Actions Section */}
      <div>
        <div className="tw-mb-2 tw-text-sm tw-font-medium tw-text-neutral-800">
          Actions
        </div>
        <div className="tw-space-y-4">
          {actions.map((action, index) => {
            return (
              <div>
                {action.type ===
                OptimizationActionType.PLAN_PRICE_ADJUSTMENT ? (
                  <div className="tw-flex tw-flex-wrap tw-gap-1">
                    <Chip>Plan Price Adjustment</Chip>
                    <Chip>
                      {action.adjustment_type ===
                      OptimizationPriceAdjustmentType.INCREASE_BY_PERCENTAGE
                        ? 'Increase by percentage'
                        : action.adjustment_type ===
                            OptimizationPriceAdjustmentType.INCREASE_BY_FIXED_AMOUNT
                          ? 'Increase by fixed amount'
                          : action.adjustment_type ===
                              OptimizationPriceAdjustmentType.DECREASE_BY_PERCENTAGE
                            ? 'Decrease by percentage'
                            : action.adjustment_type ===
                                OptimizationPriceAdjustmentType.DECREASE_BY_FIXED_AMOUNT
                              ? 'Decrease by fixed amount'
                              : 'Set Fixed Price'}
                    </Chip>
                    <Chip>
                      {action.adjustment_value ?? 0}
                      {action.adjustment_type ===
                        OptimizationPriceAdjustmentType.INCREASE_BY_PERCENTAGE ||
                      action.adjustment_type ===
                        OptimizationPriceAdjustmentType.DECREASE_BY_PERCENTAGE
                        ? ' %'
                        : ''}
                    </Chip>
                  </div>
                ) : action.type === OptimizationActionType.PLAN_CHANGE ? (
                  action.plans.map((plan, idx: number) => (
                    <div
                      key={idx}
                      className="tw-flex tw-flex-wrap tw-items-center tw-gap-mi"
                    >
                      <div className="tw-basis-12 tw-text-sm tw-text-neutral-600">
                        {idx === 0 ? <>Change</> : <>And</>}
                      </div>
                      <Chip>{getPlanLabel(plan.current_plan)}</Chip>
                      <span className="tw-text-sm tw-text-neutral-600">to</span>
                      <Chip>{getPlanLabel(plan.target_plan)}</Chip>
                    </div>
                  ))
                ) : (
                  <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-mi">
                    <Chip>Addon Price Adjustment</Chip>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const ActionCard: React.FC<ActionCardProps> = ({
  action,
  isExpanded,
  onToggle,
  onEdit,
  onRemove,
}) => {
  const summary = formatSummary(action, action.condition);

  return (
    <div className="tw-rounded tw-border tw-border-neutral-100 tw-bg-neutral-50">
      <div
        className="tw-flex tw-cursor-pointer tw-items-start tw-justify-between tw-p-4"
        onClick={onToggle}
      >
        <div className="tw-flex tw-flex-1 tw-flex-col tw-gap-1">
          <span className="tw-text-sm tw-font-medium tw-text-neutral-900">
            {action.display_name}
          </span>
          <div className="tw-line-clamp-1 tw-text-xs tw-text-neutral-500">
            {summary}
          </div>
        </div>
        <div className="tw-flex tw-items-center tw-gap-2">
          <SButton variant="danger-outline" onClick={onRemove}>
            Remove
          </SButton>
          <SButton variant="neutral" onClick={onEdit}>
            Edit
          </SButton>
          <SIcon
            name={isExpanded ? 'chevron-up' : 'chevron-down'}
            className="tw-text-neutral-500"
          />
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="tw-space-y-4 tw-rounded-b tw-bg-white tw-p-4">
          <ActionCardDetails actions={[action]} />
        </div>
      )}
    </div>
  );
};

export const OptimizationActions: React.FC = () => {
  const history = useHistory();
  const createUrl = useUrl();
  const params = useParams<{ optId: string; step: string }>();
  const { optimization } = useOptimizationEditor();
  const { setOptimization } = useOptimizationEditor();
  const baseAuthPath = '/company/:companyKey/app/:appKey';
  const action = useMemo(() => {
    return (
      optimization.actions?.[0] ??
      (OptimizationSliceHelper.createNewSimpleAction() as OptimizationAction)
    );
  }, [optimization.actions]);
  const updateAction = useCallback(
    (updated: OptimizationAction) => {
      setOptimization((prev) =>
        produce(prev, (draft) => {
          draft.actions ??= [];
          draft.actions[0] = updated;
        })
      );
    },
    [setOptimization]
  );

  // Determine initial state of extendedActions based on first action's condition
  // If condition is empty ({and:[]}), extendedActions should be false
  const initialExtendedActions = useMemo(() => {
    const firstAction = optimization.actions?.[0];
    if (!firstAction?.condition) {
      return false;
    }
    const groupConditions = getGroupConditions(firstAction.condition);
    // If condition has no actual conditions (empty and/or array), it's a simple action
    return Boolean(groupConditions?.length);
  }, [optimization.actions]);

  const [extendedActions, setExtendedActions] = useState(
    initialExtendedActions
  );

  const [isSwitchModalOpen, setIsSwitchModalOpen] = useState(false);
  const [pendingExtendedActions, setPendingExtendedActions] = useState<
    boolean | null
  >(null);

  // Sync extendedActions state when the condition changes
  useEffect(() => {
    setExtendedActions(initialExtendedActions);
  }, [initialExtendedActions]);

  // Track expanded state for each action
  const [expandedStates, setExpandedStates] = useState<
    Record<number, boolean | undefined>
  >({});

  const toggleExpanded = (index: number) => {
    setExpandedStates((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const drawer = (
    <Route
      path={`${baseAuthPath}${AuthorizedPaths.pricingOptimizationEditActionsEdit}`}
    >
      <OptimizationActionDrawer />
    </Route>
  );

  const createRuleBtn = (
    <SButton
      variant="primary-outline"
      icon={<SIcon name="plus" />}
      onClick={() => {
        history.replace(
          createUrl(AuthorizedPaths.pricingOptimizationEditActionsEdit, {
            ...params,
            actionIdx: optimization.actions?.length ?? 0,
          })
        );
      }}
    >
      Create rule
    </SButton>
  );

  const emptyPlaceholder = (
    <div className="tw-rounded tw-border tw-border-dashed tw-border-neutral-200 tw-p-6 tw-text-center">
      <div className="tw-my-4">
        <div className="tw-mb-1 tw-font-medium tw-text-cbrneutral-900">
          Build your first pricing rule
        </div>
        <div className="tw-text-sm tw-text-cbrneutral-600">
          Create rules to apply different pricing actions based on subscriber
          attributes.
        </div>
      </div>
      {createRuleBtn}
    </div>
  );

  const handleDeleteAction = (idx: number) => {
    if (!optimization.actions) return;
    setOptimization(
      produce(optimization, (draft) => {
        if (draft.actions) {
          draft.actions.splice(idx, 1);
        }
      })
    );
    // Remove from expanded states
    setExpandedStates((prev) => {
      const newState = { ...prev };
      delete newState[idx];
      // Reindex remaining states
      const reindexed: Record<number, boolean | undefined> = {};
      Object.keys(newState).forEach((key) => {
        const oldIdx = parseInt(key, 10);
        if (oldIdx > idx) {
          reindexed[oldIdx - 1] = newState[oldIdx];
        } else {
          reindexed[oldIdx] = newState[oldIdx];
        }
      });
      return reindexed;
    });
  };

  const handleEditAction = (idx: number) => {
    history.replace(
      createUrl(AuthorizedPaths.pricingOptimizationEditActionsEdit, {
        ...params,
        actionIdx: idx,
      })
    );
  };

  // Helper function to check if an action is actually configured with meaningful data
  const isActionConfigured = (
    action: OptimizationAction | undefined
  ): boolean => {
    if (!action?.type) {
      return false;
    }

    if (action.type === OptimizationActionType.PLAN_PRICE_ADJUSTMENT) {
      // Check if adjustment_type and adjustment_value are set
      return (
        action.adjustment_type !== undefined &&
        action.adjustment_value !== undefined
      );
    }

    if (action.type === OptimizationActionType.PLAN_CHANGE) {
      // Check if there are plans with both current_plan and target_plan set
      return (
        (action.plans?.length ?? 0) > 0 &&
        action.plans.some((plan) => plan.current_plan && plan.target_plan)
      );
    }

    return false;
  };

  const handleSwitchChange = (checked: boolean) => {
    // If switching to the same value, do nothing
    if (checked === extendedActions) {
      return;
    }

    // Check if there are actual changes to discard
    const hasChangesToDiscard = extendedActions
      ? // Extended mode: check if there are any actions configured
        (optimization.actions?.length ?? 0) > 0 &&
        optimization.actions?.some((action) => isActionConfigured(action)) ===
          true
      : // Simple mode: check if action has been configured with meaningful data
        isActionConfigured(optimization.actions?.[0]);

    if (!hasChangesToDiscard) {
      // No changes to discard, switch directly without showing modal
      setExtendedActions(checked);
      setOptimization((prev) =>
        produce(prev, (draft) => {
          draft.actions = [];
        })
      );
      setExpandedStates({});
      return;
    }

    // Show modal to confirm the change
    setPendingExtendedActions(checked);
    setIsSwitchModalOpen(true);
  };

  const handleConfirmSwitch = () => {
    if (pendingExtendedActions !== null) {
      setExtendedActions(pendingExtendedActions);
      // Reset actions when switching modes
      setOptimization((prev) =>
        produce(prev, (draft) => {
          draft.actions = [];
        })
      );
      setExpandedStates({});
    }
    setIsSwitchModalOpen(false);
    setPendingExtendedActions(null);
  };

  const handleCancelSwitch = () => {
    setIsSwitchModalOpen(false);
    setPendingExtendedActions(null);
  };

  const handleModalOpenChange = (open: boolean) => {
    setIsSwitchModalOpen(open);
    if (!open) {
      // Reset pending state when modal is closed (via X button or backdrop)
      setPendingExtendedActions(null);
    }
  };

  const list = optimization.actions?.map((action, idx) => (
    <ActionCard
      key={idx}
      action={action}
      isExpanded={expandedStates[idx]}
      onToggle={() => toggleExpanded(idx)}
      onEdit={() => handleEditAction(idx)}
      onRemove={() => handleDeleteAction(idx)}
    />
  ));

  return (
    <div className="tw-flex tw-w-11/12 tw-flex-col tw-gap-md tw-rounded-lg tw-border tw-border-neutral-200 tw-bg-white tw-p-md">
      <div>
        <div className="tw-mb-1 tw-font-medium">Define your optimizations</div>
        <div className="tw-text-sm">Choose subscriptions to optimize</div>
      </div>

      <div>
        <SNotification
          icon={null}
          // @ts-ignore
          title={
            <div className="tw-flex tw-justify-between">
              Apply optimizations based on conditional logic?
              <SSwitch
                checked={extendedActions}
                onCheckedChange={handleSwitchChange}
              />
            </div>
          }
          // @ts-ignore
          description={
            <span>
              Create multiple rules with different actions. For example: Basic
              plan customers get 10% discount, Premium customers upgrade to
              Enterprise plan
            </span>
          }
          variant="neutral"
          hasBorder={false}
        />
      </div>

      <div>
        {!extendedActions ? (
          <OptimizationActionForm
            value={action}
            onChange={updateAction}
            actionsOnly
          />
        ) : (
          <>
            {optimization.actions && optimization.actions.length > 0 ? (
              <>
                <div className="tw-flex tw-flex-col tw-gap-4">{list}</div>
                <div className="tw-mt-md">{createRuleBtn}</div>
              </>
            ) : (
              emptyPlaceholder
            )}
          </>
        )}
        {drawer}
      </div>

      <SModal open={isSwitchModalOpen} onOpenChange={handleModalOpenChange}>
        <SModal.Content size="regular" space="regular">
          <SModal.Header showCloseButton>
            <SModal.Title textSize="regular">
              <div className="tw-px-sm tw-pt-sm tw-font-medium">
                Switching Modes Will Discard Current Changes
              </div>
            </SModal.Title>
          </SModal.Header>
          <SModal.Body>
            <div className="tw-flex tw-w-full tw-flex-col tw-gap-md tw-p-sm">
              <div className="tw-text-sm tw-text-neutral-600">
                Enabling / disabling attribute-based actions will reset the
                renewal optimizations that you have configured in this step. You
                will need to set them up again.
              </div>
              <div className="tw-flex tw-justify-end tw-gap-sm">
                <SButton variant="neutral" onClick={handleCancelSwitch}>
                  Cancel
                </SButton>
                <SButton variant="danger" onClick={handleConfirmSwitch}>
                  Continue & Reset
                </SButton>
              </div>
            </div>
          </SModal.Body>
        </SModal.Content>
      </SModal>
    </div>
  );
};
